import type { Molecule3D } from "@/components/organic/BallStickViewer";

/**
 * Library of essential molecules for the Molecule Viewer tool.
 *
 * Each entry carries:
 *  - metadata (name, formula, category, blurb)
 *  - a 3D ball-and-stick representation reused by BallStickViewer
 *  - a 2D skeletal/Lewis representation used by SkeletalViewer
 *
 * 2D coordinates use a viewBox of roughly -100..100 on both axes; the renderer
 * auto-fits. Implicit hydrogens on carbons are NOT drawn in 2D (skeletal style),
 * but heteroatom hydrogens (O–H, N–H) are shown explicitly.
 */

export type Atom2D = {
  el: "C" | "H" | "O" | "N";
  x: number;
  y: number;
  /** If true, render the element label even for carbon. */
  showLabel?: boolean;
};

export type Bond2D = {
  /** Index into atoms2d. */
  a: number;
  b: number;
  order?: 1 | 2 | 3;
  /** Optional aromatic ring marker - renders inner circle for benzene. */
  aromatic?: boolean;
};

export type Molecule2D = {
  atoms: Atom2D[];
  bonds: Bond2D[];
  /** Optional aromatic ring center+radius for benzene-style inner circle. */
  aromaticRing?: { cx: number; cy: number; r: number };
};

export type MoleculeCategory = "Hydrocarbons" | "Inorganic" | "Functional groups" | "Biomolecules";

export type LibraryMolecule = {
  id: string;
  name: string;
  formula: string;
  category: MoleculeCategory;
  blurb: string;
  geometry: string;
  view2d: Molecule2D;
  view3d: Molecule3D;
};

// ---------- helpers for 3D building ----------
// All coordinates are in Ångström-ish units; the viewer auto-scales.

const tet = (d = 1.09) => {
  // Four tetrahedral unit vectors scaled by d
  const k = d / Math.sqrt(3);
  return [
    [k, k, k],
    [-k, -k, k],
    [-k, k, -k],
    [k, -k, -k],
  ] as Array<[number, number, number]>;
};

// ---------- molecules ----------

export const MOLECULE_LIBRARY: LibraryMolecule[] = [
  // ===== Methane =====
  {
    id: "methane",
    name: "Methane",
    formula: "CH₄",
    category: "Hydrocarbons",
    blurb: "Simplest alkane and the main component of natural gas.",
    geometry: "Tetrahedral",
    view2d: {
      atoms: [
        { el: "C", x: 0, y: 0, showLabel: true },
        { el: "H", x: 0, y: -45 },
        { el: "H", x: 45, y: 0 },
        { el: "H", x: 0, y: 45 },
        { el: "H", x: -45, y: 0 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 0, b: 2 },
        { a: 0, b: 3 },
        { a: 0, b: 4 },
      ],
    },
    view3d: (() => {
      const hs = tet(1.09);
      return {
        atoms: [
          { id: "c", el: "C", pos: [0, 0, 0] },
          { id: "h1", el: "H", pos: hs[0] },
          { id: "h2", el: "H", pos: hs[1] },
          { id: "h3", el: "H", pos: hs[2] },
          { id: "h4", el: "H", pos: hs[3] },
        ],
        bonds: [
          { from: "c", to: "h1" },
          { from: "c", to: "h2" },
          { from: "c", to: "h3" },
          { from: "c", to: "h4" },
        ],
      };
    })(),
  },

  // ===== Ethane =====
  {
    id: "ethane",
    name: "Ethane",
    formula: "C₂H₆",
    category: "Hydrocarbons",
    blurb: "Two-carbon alkane; second simplest hydrocarbon.",
    geometry: "Tetrahedral at each C",
    view2d: {
      atoms: [
        { el: "C", x: -25, y: 0, showLabel: true },
        { el: "C", x: 25, y: 0, showLabel: true },
        { el: "H", x: -55, y: -25 },
        { el: "H", x: -55, y: 25 },
        { el: "H", x: -25, y: 35 },
        { el: "H", x: 55, y: -25 },
        { el: "H", x: 55, y: 25 },
        { el: "H", x: 25, y: 35 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 0, b: 2 },
        { a: 0, b: 3 },
        { a: 0, b: 4 },
        { a: 1, b: 5 },
        { a: 1, b: 6 },
        { a: 1, b: 7 },
      ],
    },
    view3d: {
      atoms: [
        { id: "c1", el: "C", pos: [-0.77, 0, 0] },
        { id: "c2", el: "C", pos: [0.77, 0, 0] },
        { id: "h1", el: "H", pos: [-1.16, 1.02, 0] },
        { id: "h2", el: "H", pos: [-1.16, -0.51, 0.88] },
        { id: "h3", el: "H", pos: [-1.16, -0.51, -0.88] },
        { id: "h4", el: "H", pos: [1.16, -1.02, 0] },
        { id: "h5", el: "H", pos: [1.16, 0.51, 0.88] },
        { id: "h6", el: "H", pos: [1.16, 0.51, -0.88] },
      ],
      bonds: [
        { from: "c1", to: "c2" },
        { from: "c1", to: "h1" },
        { from: "c1", to: "h2" },
        { from: "c1", to: "h3" },
        { from: "c2", to: "h4" },
        { from: "c2", to: "h5" },
        { from: "c2", to: "h6" },
      ],
    },
  },

  // ===== Ethene =====
  {
    id: "ethene",
    name: "Ethene (Ethylene)",
    formula: "C₂H₄",
    category: "Hydrocarbons",
    blurb: "Simplest alkene; key feedstock for polyethylene plastics.",
    geometry: "Trigonal planar at each C",
    view2d: {
      atoms: [
        { el: "C", x: -25, y: 0, showLabel: true },
        { el: "C", x: 25, y: 0, showLabel: true },
        { el: "H", x: -55, y: -25 },
        { el: "H", x: -55, y: 25 },
        { el: "H", x: 55, y: -25 },
        { el: "H", x: 55, y: 25 },
      ],
      bonds: [
        { a: 0, b: 1, order: 2 },
        { a: 0, b: 2 },
        { a: 0, b: 3 },
        { a: 1, b: 4 },
        { a: 1, b: 5 },
      ],
    },
    view3d: {
      atoms: [
        { id: "c1", el: "C", pos: [-0.67, 0, 0] },
        { id: "c2", el: "C", pos: [0.67, 0, 0] },
        { id: "h1", el: "H", pos: [-1.23, 0.93, 0] },
        { id: "h2", el: "H", pos: [-1.23, -0.93, 0] },
        { id: "h3", el: "H", pos: [1.23, 0.93, 0] },
        { id: "h4", el: "H", pos: [1.23, -0.93, 0] },
      ],
      bonds: [
        { from: "c1", to: "c2", order: 2 },
        { from: "c1", to: "h1" },
        { from: "c1", to: "h2" },
        { from: "c2", to: "h3" },
        { from: "c2", to: "h4" },
      ],
    },
  },

  // ===== Ethyne =====
  {
    id: "ethyne",
    name: "Ethyne (Acetylene)",
    formula: "C₂H₂",
    category: "Hydrocarbons",
    blurb: "Simplest alkyne; burns hot in oxyacetylene torches.",
    geometry: "Linear",
    view2d: {
      atoms: [
        { el: "H", x: -75, y: 0 },
        { el: "C", x: -25, y: 0, showLabel: true },
        { el: "C", x: 25, y: 0, showLabel: true },
        { el: "H", x: 75, y: 0 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 1, b: 2, order: 3 },
        { a: 2, b: 3 },
      ],
    },
    view3d: {
      atoms: [
        { id: "c1", el: "C", pos: [-0.6, 0, 0] },
        { id: "c2", el: "C", pos: [0.6, 0, 0] },
        { id: "h1", el: "H", pos: [-1.66, 0, 0] },
        { id: "h2", el: "H", pos: [1.66, 0, 0] },
      ],
      bonds: [
        { from: "c1", to: "c2", order: 2 }, // viewer caps at order 2 visually; triple shown via 3D context
        { from: "c1", to: "h1" },
        { from: "c2", to: "h2" },
      ],
    },
  },

  // ===== Water =====
  {
    id: "water",
    name: "Water",
    formula: "H₂O",
    category: "Inorganic",
    blurb: "The universal solvent; bent geometry due to two lone pairs.",
    geometry: "Bent (~104.5°)",
    view2d: {
      atoms: [
        { el: "O", x: 0, y: 0, showLabel: true },
        { el: "H", x: -40, y: 25 },
        { el: "H", x: 40, y: 25 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 0, b: 2 },
      ],
    },
    view3d: {
      atoms: [
        { id: "o", el: "O", pos: [0, 0, 0] },
        { id: "h1", el: "H", pos: [-0.76, 0.59, 0] },
        { id: "h2", el: "H", pos: [0.76, 0.59, 0] },
      ],
      bonds: [
        { from: "o", to: "h1" },
        { from: "o", to: "h2" },
      ],
    },
  },

  // ===== Ammonia =====
  {
    id: "ammonia",
    name: "Ammonia",
    formula: "NH₃",
    category: "Inorganic",
    blurb: "Trigonal pyramidal; key nitrogen feedstock for fertilizers.",
    geometry: "Trigonal pyramidal",
    view2d: {
      atoms: [
        { el: "N", x: 0, y: 0, showLabel: true },
        { el: "H", x: 0, y: -45 },
        { el: "H", x: -40, y: 25 },
        { el: "H", x: 40, y: 25 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 0, b: 2 },
        { a: 0, b: 3 },
      ],
    },
    view3d: {
      atoms: [
        { id: "n", el: "N", pos: [0, 0, 0] },
        { id: "h1", el: "H", pos: [0, 0.94, 0.34] },
        { id: "h2", el: "H", pos: [-0.81, -0.47, 0.34] },
        { id: "h3", el: "H", pos: [0.81, -0.47, 0.34] },
      ],
      bonds: [
        { from: "n", to: "h1" },
        { from: "n", to: "h2" },
        { from: "n", to: "h3" },
      ],
    },
  },

  // ===== CO2 =====
  {
    id: "carbon-dioxide",
    name: "Carbon Dioxide",
    formula: "CO₂",
    category: "Inorganic",
    blurb: "Linear molecule; product of combustion and respiration.",
    geometry: "Linear",
    view2d: {
      atoms: [
        { el: "O", x: -50, y: 0, showLabel: true },
        { el: "C", x: 0, y: 0, showLabel: true },
        { el: "O", x: 50, y: 0, showLabel: true },
      ],
      bonds: [
        { a: 0, b: 1, order: 2 },
        { a: 1, b: 2, order: 2 },
      ],
    },
    view3d: {
      atoms: [
        { id: "c", el: "C", pos: [0, 0, 0] },
        { id: "o1", el: "O", pos: [-1.16, 0, 0] },
        { id: "o2", el: "O", pos: [1.16, 0, 0] },
      ],
      bonds: [
        { from: "c", to: "o1", order: 2 },
        { from: "c", to: "o2", order: 2 },
      ],
    },
  },

  // ===== Benzene =====
  {
    id: "benzene",
    name: "Benzene",
    formula: "C₆H₆",
    category: "Hydrocarbons",
    blurb: "Aromatic ring with delocalized π electrons; planar hexagon.",
    geometry: "Planar hexagonal (aromatic)",
    view2d: (() => {
      const r = 35;
      const atoms: Atom2D[] = [];
      const bonds: Bond2D[] = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        atoms.push({ el: "C", x: Math.cos(a) * r, y: Math.sin(a) * r });
      }
      // H's pointing outward
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        atoms.push({ el: "H", x: Math.cos(a) * (r + 25), y: Math.sin(a) * (r + 25) });
      }
      for (let i = 0; i < 6; i++) {
        bonds.push({ a: i, b: (i + 1) % 6, aromatic: true });
        bonds.push({ a: i, b: i + 6 });
      }
      return { atoms, bonds, aromaticRing: { cx: 0, cy: 0, r: r * 0.55 } };
    })(),
    view3d: (() => {
      const r = 1.39;
      const atoms: Molecule3D["atoms"] = [];
      const bonds: Molecule3D["bonds"] = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        atoms.push({ id: `c${i}`, el: "C", pos: [Math.cos(a) * r, Math.sin(a) * r, 0] });
      }
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        atoms.push({ id: `h${i}`, el: "H", pos: [Math.cos(a) * (r + 1.08), Math.sin(a) * (r + 1.08), 0] });
      }
      // Alternate single/double bonds (Kekulé) for visual hint
      for (let i = 0; i < 6; i++) {
        bonds.push({ from: `c${i}`, to: `c${(i + 1) % 6}`, order: i % 2 === 0 ? 2 : 1 });
        bonds.push({ from: `c${i}`, to: `h${i}` });
      }
      return { atoms, bonds };
    })(),
  },

  // ===== Cyclohexane (chair drawn flat in 2D for simplicity) =====
  {
    id: "cyclohexane",
    name: "Cyclohexane",
    formula: "C₆H₁₂",
    category: "Hydrocarbons",
    blurb: "Saturated six-membered ring; adopts the famous chair conformation.",
    geometry: "Chair (3D)",
    view2d: (() => {
      const r = 35;
      const atoms: Atom2D[] = [];
      const bonds: Bond2D[] = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        atoms.push({ el: "C", x: Math.cos(a) * r, y: Math.sin(a) * r });
      }
      for (let i = 0; i < 6; i++) {
        bonds.push({ a: i, b: (i + 1) % 6 });
      }
      return { atoms, bonds };
    })(),
    view3d: (() => {
      // Chair conformation: alternating z up/down
      const r = 1.5;
      const atoms: Molecule3D["atoms"] = [];
      const bonds: Molecule3D["bonds"] = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        const z = i % 2 === 0 ? 0.25 : -0.25;
        atoms.push({ id: `c${i}`, el: "C", pos: [Math.cos(a) * r, Math.sin(a) * r, z] });
      }
      // Two H per C (axial + equatorial, simplified)
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        const z = i % 2 === 0 ? 0.25 : -0.25;
        atoms.push({ id: `ha${i}`, el: "H", pos: [Math.cos(a) * r, Math.sin(a) * r, z + (i % 2 === 0 ? 1.0 : -1.0)] });
        atoms.push({ id: `he${i}`, el: "H", pos: [Math.cos(a) * (r + 0.95), Math.sin(a) * (r + 0.95), z * 0.3] });
      }
      for (let i = 0; i < 6; i++) {
        bonds.push({ from: `c${i}`, to: `c${(i + 1) % 6}` });
        bonds.push({ from: `c${i}`, to: `ha${i}` });
        bonds.push({ from: `c${i}`, to: `he${i}` });
      }
      return { atoms, bonds };
    })(),
  },

  // ===== Ethanol =====
  {
    id: "ethanol",
    name: "Ethanol",
    formula: "C₂H₅OH",
    category: "Functional groups",
    blurb: "Alcohol found in fermented drinks and biofuels.",
    geometry: "Tetrahedral at C; bent at O",
    view2d: {
      atoms: [
        { el: "C", x: -45, y: 0, showLabel: true },
        { el: "C", x: 0, y: 0, showLabel: true },
        { el: "O", x: 45, y: 0, showLabel: true },
        { el: "H", x: 75, y: -25 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 1, b: 2 },
        { a: 2, b: 3 },
      ],
    },
    view3d: {
      atoms: [
        { id: "c1", el: "C", pos: [-1.25, 0.2, 0] },
        { id: "c2", el: "C", pos: [0, -0.55, 0] },
        { id: "o", el: "O", pos: [1.18, 0.32, 0] },
        { id: "ho", el: "H", pos: [1.95, -0.22, 0] },
        { id: "h1", el: "H", pos: [-1.3, 0.85, 0.88] },
        { id: "h2", el: "H", pos: [-1.3, 0.85, -0.88] },
        { id: "h3", el: "H", pos: [-2.13, -0.43, 0] },
        { id: "h4", el: "H", pos: [0, -1.2, 0.88] },
        { id: "h5", el: "H", pos: [0, -1.2, -0.88] },
      ],
      bonds: [
        { from: "c1", to: "c2" },
        { from: "c2", to: "o" },
        { from: "o", to: "ho" },
        { from: "c1", to: "h1" },
        { from: "c1", to: "h2" },
        { from: "c1", to: "h3" },
        { from: "c2", to: "h4" },
        { from: "c2", to: "h5" },
      ],
    },
  },

  // ===== Acetic acid =====
  {
    id: "acetic-acid",
    name: "Acetic Acid",
    formula: "CH₃COOH",
    category: "Functional groups",
    blurb: "Carboxylic acid responsible for the sour taste of vinegar.",
    geometry: "Trigonal planar at carbonyl C",
    view2d: {
      atoms: [
        { el: "C", x: -50, y: 0, showLabel: true },
        { el: "C", x: 0, y: 0, showLabel: true },
        { el: "O", x: 25, y: -40, showLabel: true },
        { el: "O", x: 45, y: 25, showLabel: true },
        { el: "H", x: 85, y: 10 },
      ],
      bonds: [
        { a: 0, b: 1 },
        { a: 1, b: 2, order: 2 },
        { a: 1, b: 3 },
        { a: 3, b: 4 },
      ],
    },
    view3d: {
      atoms: [
        { id: "c1", el: "C", pos: [-1.3, 0, 0] },
        { id: "c2", el: "C", pos: [0, 0.5, 0] },
        { id: "o1", el: "O", pos: [0.05, 1.74, 0] },
        { id: "o2", el: "O", pos: [1.13, -0.18, 0] },
        { id: "ho", el: "H", pos: [1.92, 0.36, 0] },
        { id: "h1", el: "H", pos: [-1.3, -0.65, 0.88] },
        { id: "h2", el: "H", pos: [-1.3, -0.65, -0.88] },
        { id: "h3", el: "H", pos: [-2.18, 0.62, 0] },
      ],
      bonds: [
        { from: "c1", to: "c2" },
        { from: "c2", to: "o1", order: 2 },
        { from: "c2", to: "o2" },
        { from: "o2", to: "ho" },
        { from: "c1", to: "h1" },
        { from: "c1", to: "h2" },
        { from: "c1", to: "h3" },
      ],
    },
  },

  // ===== Glucose (simplified open-chain Fischer-style 2D, 3D approximated) =====
  {
    id: "glucose",
    name: "Glucose (open chain)",
    formula: "C₆H₁₂O₆",
    category: "Biomolecules",
    blurb: "Aldohexose sugar; the body's primary energy source.",
    geometry: "Open-chain aldohexose",
    view2d: (() => {
      // Vertical Fischer-like projection
      const atoms: Atom2D[] = [];
      const bonds: Bond2D[] = [];
      // Chain C1 (top, aldehyde) → C6 (bottom, CH2OH)
      for (let i = 0; i < 6; i++) {
        atoms.push({ el: "C", x: 0, y: -75 + i * 30, showLabel: true });
      }
      for (let i = 0; i < 5; i++) bonds.push({ a: i, b: i + 1 });

      // C1 aldehyde: =O up-left, H up-right
      atoms.push({ el: "O", x: -30, y: -95, showLabel: true }); // 6
      atoms.push({ el: "H", x: 30, y: -95 }); // 7
      bonds.push({ a: 0, b: 6, order: 2 });
      bonds.push({ a: 0, b: 7 });

      // C2..C5 each: OH on one side, H on other (alternating)
      const ohSides = [1, -1, 1, 1]; // simplified D-glucose pattern
      for (let i = 0; i < 4; i++) {
        const cIdx = i + 1; // C2..C5
        const y = -75 + cIdx * 30;
        const ohSide = ohSides[i];
        const oIdx = atoms.length;
        atoms.push({ el: "O", x: 30 * ohSide, y, showLabel: true });
        const hOIdx = atoms.length;
        atoms.push({ el: "H", x: 50 * ohSide, y: y - 10 });
        const hIdx = atoms.length;
        atoms.push({ el: "H", x: -30 * ohSide, y });
        bonds.push({ a: cIdx, b: oIdx });
        bonds.push({ a: oIdx, b: hOIdx });
        bonds.push({ a: cIdx, b: hIdx });
      }

      // C6 (CH2OH) at bottom
      const oIdx = atoms.length;
      atoms.push({ el: "O", x: 30, y: 75, showLabel: true });
      const hOIdx = atoms.length;
      atoms.push({ el: "H", x: 55, y: 70 });
      bonds.push({ a: 5, b: oIdx });
      bonds.push({ a: oIdx, b: hOIdx });

      return { atoms, bonds };
    })(),
    view3d: (() => {
      // Simplified zig-zag chain in 3D, hydroxyl branches off perpendicular
      const atoms: Molecule3D["atoms"] = [];
      const bonds: Molecule3D["bonds"] = [];
      const step = 1.25;
      for (let i = 0; i < 6; i++) {
        const x = (i - 2.5) * step * 0.7;
        const y = (i % 2 === 0 ? 0.3 : -0.3);
        atoms.push({ id: `c${i}`, el: "C", pos: [x, y, 0] });
      }
      for (let i = 0; i < 5; i++) bonds.push({ from: `c${i}`, to: `c${i + 1}` });

      // C1 aldehyde
      atoms.push({ id: "o_ald", el: "O", pos: [(0 - 2.5) * step * 0.7 - 0.6, 1.4, 0] });
      bonds.push({ from: "c0", to: "o_ald", order: 2 });
      atoms.push({ id: "h_ald", el: "H", pos: [(0 - 2.5) * step * 0.7 + 0.6, 1.4, 0] });
      bonds.push({ from: "c0", to: "h_ald" });

      // OH on C2..C5
      for (let i = 1; i <= 4; i++) {
        const c = atoms[i].pos;
        const dy = i % 2 === 0 ? 1.0 : -1.0;
        const oId = `o${i}`;
        const hId = `ho${i}`;
        atoms.push({ id: oId, el: "O", pos: [c[0], c[1] + dy * 0.3, dy * 1.0] });
        atoms.push({ id: hId, el: "H", pos: [c[0] + 0.3, c[1] + dy * 0.4, dy * 1.7] });
        bonds.push({ from: `c${i}`, to: oId });
        bonds.push({ from: oId, to: hId });
      }

      // C6 OH
      const c5 = atoms[5].pos;
      atoms.push({ id: "o6", el: "O", pos: [c5[0] + 0.9, c5[1] - 0.3, 0.9] });
      atoms.push({ id: "ho6", el: "H", pos: [c5[0] + 1.5, c5[1] - 0.3, 1.4] });
      bonds.push({ from: "c5", to: "o6" });
      bonds.push({ from: "o6", to: "ho6" });

      return { atoms, bonds };
    })(),
  },
];

export const getMoleculeById = (id: string) =>
  MOLECULE_LIBRARY.find((m) => m.id === id);